import { clients } from 'lib/https';
import { ConfigurationSettings } from 'models/configuration-settings';

/**
 * Database Connection Service
 * 
 * Handles API calls for managing database connections (PostgreSQL, MongoDB, MySQL)
 */

export interface DatabaseConnectionRequest {
    databaseType: 'postgres' | 'mongodb' | 'mysql';
    scope: 'user' | 'organization';
    host?: string;
    port?: number;
    username?: string;
    password?: string;
    databaseName?: string;
    connectionString?: string;
    extra?: Record<string, any> | null;
}

export interface DatabaseConnectionInfo {
    connectionString: string | null;
    host: string;
    port: number;
    username: string;
    password: string;
    databaseName: string;
    extra: Record<string, any> | null;
}

export interface DatabaseConnection {
    connectionId: string;
    scope: string;
    databaseType: string;
    displayName: string;
    connectionInfo: DatabaseConnectionInfo;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

// List view interface (simplified for list endpoints)
export interface DatabaseConnectionListItem {
    ConnectionId: string;
    Scope: string;
    DatabaseType: string;
    DisplayName: string;
    IsActive: boolean;
}

export interface UpsertDatabaseConnectionResponse {
    connectionId: string;
    message: string;
}

export interface DeleteDatabaseConnectionResponse {
    success: boolean;
    message: string;
}

/**
 * Create or update a database connection
 */
export const upsertDatabaseConnection = async (
    config: ConfigurationSettings,
    payload: DatabaseConnectionRequest
): Promise<UpsertDatabaseConnectionResponse> => {
    const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
    const url = `${baseUrl}/taskforce/api/db-connections/upsert`;
    
    // eslint-disable-next-line no-console
    console.log('Upserting database connection at URL:', url);
    
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

/**
 * Get a database connection by ID
 */
export const getDatabaseConnection = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<DatabaseConnection> => {
    const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
    const url = `${baseUrl}/taskforce/api/db-connections/${connectionId}`;
    
    // eslint-disable-next-line no-console
    console.log('Getting database connection from URL:', url);
    
    return clients.get(url, config.blocksKey);
};

/**
 * Get list of database connections (simplified list view with active status)
 */
export const listDatabaseConnections = async (
    config: ConfigurationSettings
): Promise<DatabaseConnectionListItem[]> => {
    const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
    const url = `${baseUrl}${config.paths.taskforce.listDBConnections}`;
    
    // eslint-disable-next-line no-console
    console.log('Getting database connections list from URL:', url);
    
    return clients.get(url, config.blocksKey);
};

/**
 * Delete a database connection
 */
export const deleteDatabaseConnection = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<DeleteDatabaseConnectionResponse> => {
    const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
    const url = `${baseUrl}/taskforce/api/db-connections/${connectionId}`;
    
    // eslint-disable-next-line no-console
    console.log('Deleting database connection at URL:', url);
    
    return clients.delete(url, config.blocksKey);
};

/**
 * Test a database connection
 */
export const testDatabaseConnection = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<{ success: boolean; message: string }> => {
    const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
    const url = `${baseUrl}/taskforce/api/db-connections/${connectionId}/test`;
    
    // eslint-disable-next-line no-console
    console.log('Testing database connection at URL:', url);
    
    return clients.post(url, config.blocksKey, JSON.stringify({}));
};

/**
 * Connect to a database connection (activate it)
 */
export const connectDatabaseConnection = async (
    config: ConfigurationSettings,
    connectionId: string,
    scope: 'user' | 'organization',
): Promise<{ success: boolean; message: string }> => {
    const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
    const url = `${baseUrl}${config.paths.taskforce.connectionDB}`;
    
    // eslint-disable-next-line no-console
    console.log('Connecting database connection at URL:', url);
    
    return clients.post(url, config.blocksKey, JSON.stringify({ connectionId, scope }));
};

/**
 * Disconnect from a database connection (deactivate it)
 */
export const disconnectDatabaseConnection = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<{ success: boolean; message: string }> => {
    const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
    const url = `${baseUrl}${config.paths.taskforce.disconnectDBConnection}`;
    
    // eslint-disable-next-line no-console
    console.log('Disconnecting database connection at URL:', url);
    
    return clients.post(url, config.blocksKey, JSON.stringify({ connectionId }));
};

